/* eslint-disable */
import React, { FC, useEffect, useState } from 'react';
import { Button, Input, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import Form from '~/components/WizardForm';
import InputsGroupLayout from '~/layouts/InputsGroupLayout';
import Section from '~/layouts/Section ';
import CustomFields from '~/components/CustomFields';
import { DocumentInput, ErrorModal, LtrInput } from '~/components';
import { Stage } from '~/types/Application';
import { useHandbook, useHandbookKeys } from '~/controllers/handbook';
import { useGetApplication, useGetPayloadWithProvided, useSubmitStep } from '~/controllers/wizard';
import { KsaCompanyInformation } from '~/types/ksa/KsaCompanyInformation';
import { merge, set } from 'lodash';
import ContentHeader from '~/components/ContentHeader';
import WizardContinueButton from '~/layouts/WizardContinueButton';
import { useHistory, useLocation } from 'react-router';

import Actions from '~/components/Actions';
import { Draft } from '~/types/Draft';
import { useBankValidation } from '~/controllers/application';
import { useSaveDraft } from '~/controllers/draft';
import { AlertOutlined, WarningOutlined } from '@ant-design/icons';
import { useIsMobile, useIsTablet } from '~/hooks/useScreenSizes';
import paths from '~/pages/paths';
import theme from '~/constants/theme';
import { useDirection } from '~/hooks/i18nHooks';
import { getTrackingId } from '~/utils/handleGetUtm';
import { InfoCircleIcon } from '~/img';

const n = (namePath: string | string[]) => ['bankAccount'].concat([namePath].flat());

const BankAccount: FC = () => {
  const { t } = useTranslation('ksaApplication');
  const [disabled, setDisabled] = useState(false);
  const [bankIntegrationRes, setBankIntegrationRes] = useState<any>({});
  const [uploadError, setUploadError] = useState<{ visible: boolean; error: any }>({
    visible: false,
    error: null,
  });
  const handbook = useHandbook();
  const history = useHistory();
  const [loadingFetchingData, setLoadingFetchingData] = useState(false);
  const handbookKeys = useHandbookKeys();
  const { data }: any = useGetApplication();
  const bankValidation = useBankValidation();
  const banks = handbook.data?.handbooks?.find((item) => item.key === handbookKeys.bank);
  const location = useLocation();
  const payload = useGetPayloadWithProvided();
  const companyInformation = payload?.companyInformation as KsaCompanyInformation;
  const [form] = Form.useForm();
  const draftMutation = useSaveDraft();
  const submitMutation = useSubmitStep();
  const isMobile = useIsTablet();
  useEffect(() => {
    if (Object?.keys(bankIntegrationRes || {})?.length) {
      form.setFieldsValue({
        bankAccount: {
          bankIntegrationRes,
        },
      });
      const formValues = form.getFieldsValue();
      set(formValues, ['bankAccount', 'bankIntegrationRes'], bankIntegrationRes);
      draftMutation.mutateAsync({ stepData: formValues });
    }
  }, [bankIntegrationRes, form]);

  async function bankDetailsSubmit(values: any) {
    const bankAccount = { ...values.bankAccount };
    delete bankAccount.bankAccount;
    delete bankAccount.verified;
    (window as any).dataLayer.push({
      event: 'event_formsubmit',
      page_ln: 'en',
      page_name: window.location.href,
      page_source: document.referrer || 'direct',
      page_section: 'Bank Details',
      trackingId: getTrackingId(data),
      user_id: localStorage.getItem('sob_temp_uid'),
    });
    if (!values?.bankAccount?.bankIntegrationRes?.stopRetry) {
      setLoadingFetchingData(true);
      const res: any = await bankValidation.mutateAsync({
        iban: values.bankAccount.iban,
        type: 'BUSINESS',
        identifications: [
          {
            type: 'COMMERCIAL_REGISTRATION',
            value: data?.payload?.thiqahFullInfo?.crNumber,
          },
          {
            type: 'UNIFIED_NUMBER',
            value: data?.payload?.thiqahFullInfo?.crNationalNumber,
          },
        ],
      });
      if (res?.bankName && res?.accountHolderName) {
        return submitMutation.mutate({
          bankAccount: {
            ...bankAccount,
            accountHolderName: res.accountHolderName,
            bankName: res.bankName,
            bankIntegrationRes: res,
          },
        });
      } else {
        setBankIntegrationRes(res);
        form.setFields([
          {
            name: ['bankAccount', 'iban'],
            errors: [t(res.userMessage)],
          },
        ]);
        (window as any).dataLayer.push({
          event: 'event_fielderror',
          page_ln: 'en',
          page_name: window.location.href,
          page_source: document.referrer || 'direct',
          form_fieldname: 'bankAccount.iban',
          form_fielderror: res.internalMessage,
          trackingId: getTrackingId(data),
          user_id: localStorage.getItem('sob_temp_uid'),
        });
        setLoadingFetchingData(false);
        return;
      }
    } else {
      submitMutation.mutate({ ...values, bankAccount });
      setLoadingFetchingData(false);
    }
    setLoadingFetchingData(false);
  }
  const dir = useDirection();

  if (!payload) return null;

  return (
    <>
      <FormWrapper>
        <Form<Draft<'bankAccount'>>
          form={form}
          name={location.pathname}
          payloadKey="bankAccount"
          stage={Stage.ksaApplication}
          onFinish={(values) => bankDetailsSubmit(values)}
          onValuesChange={(changed, values) => {
            if (Object.prototype.hasOwnProperty.call(changed.bankAccount || {}, 'iban')) {
              setBankIntegrationRes({});
              form.setFields([{ name: n(['bankIntegrationRes']), value: {} }]);
              values.bankAccount.bankIntegrationRes = {};
            }
          }}
        >
          <ContentHeader
            title={t('Bank Details')}
            subtitle={t('Add your bank account information.')}
            stageNumber={3}
          />
          <Container dir={dir} isMobile={isMobile} className="BankAccount">
            <Section formItemLast>
              <Form.ShouldUpdate>
                {({ getFieldValue }) => {
                  const bankIntegrationRes = getFieldValue(['bankAccount', 'bankIntegrationRes']);
                  return (
                    <>
                      <Typography.Paragraph className="ibanDescText">
                        {isMobile && bankIntegrationRes?.showUploadField
                          ? t('Please provide the bank account details registered under your company name. These details will be used to transfer settlements to you.')
                          : t(
                            'Get paid directly to your bank account. We’ll use this IBAN to transfer your earnings securely.'
                          )}
                      </Typography.Paragraph>
                      <Form.Item
                        className={`ibanInputWrap${bankIntegrationRes?.showUploadField ? ' ibanInputWrap--disabled' : ''}`}
                        disabled={bankIntegrationRes?.showUploadField}
                        labelText={t('Enter IBAN Number')}
                        name={n(['iban'])}
                      >
                        <LtrInput />
                      </Form.Item>
                      {!bankIntegrationRes?.showUploadField && (
                        <p className="ibanHelperText">
                          {t(
                            'Please ensure the provided IBAN number is linked to your commercial registration number.'
                          )}
                        </p>
                      )}
                    </>
                  );
                }}
              </Form.ShouldUpdate>
            </Section>
            <Form.Item name={n(['bankIntegrationRes'])} hidden initialValue={{}} />
            <Form.ShouldUpdate>
              {({ getFieldValue }) => {
                const bankIntegrationRes = getFieldValue(['bankAccount', 'bankIntegrationRes']);
                const bankLetterValue = getFieldValue(n(['files']));
                const ibanValue = !!getFieldValue(n(['iban']));
                if (bankLetterValue?.length && bankIntegrationRes?.showUploadField) {
                  setDisabled(true);
                } else if (!ibanValue && !bankIntegrationRes?.showUploadField) {
                  setDisabled(true);
                } else {
                  setDisabled(false);
                }

                return (
                  bankIntegrationRes?.showUploadField && (
                    <>
                      <AlertBox dir={dir}>
                        <WarningOutlined className="warningIcon" />
                        <div>
                          <Typography.Paragraph className="warningTitle">
                            {t('We couldn’t find an account linked to this IBAN.')}
                          </Typography.Paragraph>
                          <Typography.Paragraph className="warningSubTitle">
                            {t('Please upload proof of your IBAN.')}
                          </Typography.Paragraph>
                        </div>
                      </AlertBox>
                      <Typography.Paragraph className={'subTitle1'}>
                        {t(
                          'Please provide proof of your IBAN by uploading a stamped IBAN letter that includes the UN/CR Number and is issued within the last year'
                        )}
                      </Typography.Paragraph>

                      <Form.Item rules={[{ required: true }]} name={n(['files'])}>
                        <DocumentInput
                          labelText={t("Stamped IBAN Letter")}
                          name="photos"
                          variant="default"
                          multiple={false}
                          onUploadError={(err) => {
                            const errorToShow =
                              err?.errorCode === 'BAD_PROVIDED_FILE'
                                ? {
                                  description: t('BAD_PROVIDED_FILE_en'),
                                  arDescription: t('BAD_PROVIDED_FILE_ar'),
                                }
                                : err;
                            setUploadError({ visible: true, error: errorToShow });
                          }}
                          infoIcon={<InfoCircleIcon />}

                        />
                      </Form.Item>
                    </>
                  )
                );
              }}
            </Form.ShouldUpdate>

            <div className="ibanAddBtn">
              <WizardContinueButton
                loading={loadingFetchingData}
                id="uploadDocument_continferwueButton"
                formName={location.pathname}
                disabled={disabled}
              >
                {t('Add')}
              </WizardContinueButton>
            </div>
            {isMobile && (
              <Button
                type="text"
                style={{ width: '100%', marginTop: 20, fontWeight: 600, color: '#0069B1' }}
                onClick={() => {
                  history.push(paths.ksa.contactUs);
                }}
              >
                {t('Contact Us')}
              </Button>
            )}
          </Container>
        </Form>
      </FormWrapper>
      <ErrorModal
        visible={uploadError.visible}
        error={uploadError.error}
        onClose={() => setUploadError({ visible: false, error: null })}
      />
    </>
  );
};

const AlertBox = styled.div<any>`
  display: flex;
  flex-direction: row;
  padding: 16px 20px;
  background-color: #fcefc3;
  border-radius: 12px;
  align-items: center;
  gap: 12px;
  direction: ${({ dir }) => dir || 'ltr'};
  text-align: ${({ dir }) => (dir === 'rtl' ? 'right' : 'left')};

  .warningIcon {
    font-size: 22px;
    color: #ac8504;
    flex-shrink: 0;
  }
  .warningTitle {
    color: #070707;
    font-feature-settings: 'case' on;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 22px;
    letter-spacing: 0.14px;
    margin-bottom: 2px;
    display: inline;
  }
  .warningSubTitle {
    color: #070707;
    margin-top: 0px;
    font-feature-settings: 'case' on;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
    margin-bottom: 0px;
    display: inline;
    margin-left: ${({ dir }) => (dir === 'rtl' ? '0' : '4px')};
    margin-right: ${({ dir }) => (dir === 'rtl' ? '4px' : '0')};
  }
`;

const Container = styled.div<any>`
  width: ${({ isMobile }) => (isMobile ? '100%' : '60%')};
  max-width: 540px;

  @media (max-width: ${theme.breakpoints.md}px) {
    display: flex;
    flex-direction: column;
    flex: 1;

    .ibanAddBtn {
      margin-top: auto;
    }
  }

  .subTitle1 {
    margin-top: 16px;
    margin-bottom: 12px;
  }

  /* Description text above IBAN input — changes when upload field is visible */
  .ibanDescText {
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #070707;
    margin-bottom: 20px;
    line-height: 22px;
  }

  .ibanHelperText {
    color: #595959;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 13px;
    font-weight: 400;
    line-height: 20px;
    margin-top: 8px;
    margin-bottom: 16px;
    direction: ${({ dir }) => dir || 'ltr'};
    text-align: ${({ dir }) => (dir === 'rtl' ? 'right' : 'left')};
    display: block;
    clear: both;
    text-align: ${({ isMobile }) => (isMobile ? 'center' : '')};
  }

  /* Target only the IBAN input field - scoped via .ibanInputWrap class */
  .ibanInputWrap input,
  .ibanInputWrap .ant-input,
  .ibanInputWrap .ant-input-affix-wrapper {
    height: 52px !important;
    min-height: 52px !important;
    font-size: 15px !important;
  }

  /* Arabic (RTL) web view: reduce IBAN input min-height to 48px */
  ${({ dir }) =>
    dir === 'rtl'
      ? `
    @media (min-width: ${theme.breakpoints.md}px) {
      .ibanInputWrap input,
      .ibanInputWrap .ant-input,
      .ibanInputWrap .ant-input-affix-wrapper {
        height: 52px !important;
        min-height: 48px !important;
      }
    }
  `
      : ''}

  /* Hide the red "Iban verification failed" error when field is disabled (upload shown) */
  .ibanInputWrap--disabled {
    .ant-form-item-explain,
    .ant-form-item-explain-error {
      display: none !important;
    }
  }

  /* Target only the Add button - scoped via .ibanAddBtn class */
  .ibanAddBtn .ant-btn,
  .ibanAddBtn button {
    height: 52px !important;
    min-height: 52px !important;
    font-size: 16px !important;
    font-weight: 600 !important;
  }

  /* DocumentInput upload box — match Figma: border-radius 12px, flex column, centred icon */
  .DocumentInput {
    .ant-upload.ant-upload-select-picture-card {
      border-radius: 12px !important;
      border: 1px solid #d0d5dd !important;
      background: #f9fafb !important;
      min-height: 140px;
    }

    .DocumentInputContent {
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      align-items: center;
      gap: 20px;
      padding: 16px;
      width: 100%;
      height: 100%;
      position: relative;
      border-radius: 12px;
      background: transparent;

      .DocumentInputContent-Paragraph {
        position: absolute;
        top: 12px;
        left: 16px;
        font-size: 14px;
        font-weight: 500;
        color: #1b1b20;
        margin: 0;
        line-height: 22px;
      }

      .DocumentInputContent-UploadIcon {
        position: static;
        transform: none;
        font-size: 40px;
        margin-bottom: 4px;
      }
    }
  }

  /* Wrapper gives relative context for the SVG info icon */
  .documentInputWrapper {
    position: relative;
    width: 100%;

    /* SVG info icon — absolutely positioned at bottom-right inside the upload box */
    .uploadInfoIcon {
      position: absolute;
      bottom: 14px;
      right: 14px;
      pointer-events: none;
      z-index: 2;
      display: block;
    }
  }
`;

const FormWrapper = styled.div`
  @media (max-width: ${theme.breakpoints.md}px) {
    display: flex;
    flex-direction: column;
    flex: 1;
    margin-bottom: -10px;

    form {
      display: flex;
      flex-direction: column;
      flex: 1;
    }
  }

  @media (max-width: ${theme.breakpoints.xs}px) {
    margin-bottom: -14px;
  }
`;

export default BankAccount;
